/* eslint-disable eqeqeq */
import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import 'react-toastify/dist/ReactToastify.css';
import moment from 'moment';

import TextField from '@material-ui/core/TextField';
import Autocomplete from '@material-ui/lab/Autocomplete';
import PersonOutlineRounded from '@material-ui/icons/PersonOutlineRounded';
import AccountBalanceOutlined from '@material-ui/icons/AccountBalanceOutlined';
import { verifyLogin, apiList } from '../../helpers';
import {
  CustomField, DateInput, FormSection, SelectInput, TextInput,
} from './LoanFormFields';

// Customer and loan sections of the Add / Edit loan form. Field handlers are
// unchanged from the original form; `errors` = { field: message } shows the failed check inline.
export default function FormLoan({
  newLoan = {}, setNewLoan, loanTypes, errors = {},
}) {
  const [accessLevels, setAccessLevel] = useState([]);
  const [inputValue, setInputValue] = useState('');

  useEffect(() => {
    const accessLevel = accessLevels.find((item) => item.ifscCode === newLoan.branchIFSC);
    if (accessLevel) {
      setInputValue(accessLevel.branchName);
    }
  }, [newLoan.branchIFSC]);

  // Editing: the loan and the branch list load separately; show the loan's
  // branch name once both are in (only once, so typing a search isn't undone).
  const prefilled = useRef(false);
  useEffect(() => {
    if (prefilled.current || !newLoan.branchIFSC) return;
    const accessLevel = accessLevels.find((item) => item.ifscCode === newLoan.branchIFSC);
    if (accessLevel) {
      prefilled.current = true;
      setInputValue(accessLevel.branchName);
    }
  }, [newLoan.branchIFSC, accessLevels]);

  const getBranches = (searchQuery) => {
    const { session } = verifyLogin();

    const config = { headers: { session } };
    let url = apiList.GET_SUBSCRIBERS_BRANCHES;
    if (searchQuery) {
      url = `${url}?searchQuery=${searchQuery}`;
    }
    axios.get(url, config)
      .then((response) => {
        if (response?.data?.data) {
          setAccessLevel(response?.data?.data);
        }
      })
      .catch((error) => {
        console.log(error);
      });
  };

  useEffect(() => {
    getBranches();
  }, []);

  const set = (obj) => setNewLoan({ ...newLoan, ...obj });

  return (
    <>
      <FormSection title='Customer' icon={<PersonOutlineRounded />}>
        <TextInput
          id='custFirstName'
          label='First name'
          span={3}
          required
          error={errors.custFirstName}
          value={newLoan.custFirstName}
          onChange={(e) => set({ custFirstName: e.target.value })}
        />
        <TextInput
          id='custLastName'
          label='Last name'
          span={3}
          required
          error={errors.custLastName}
          value={newLoan.custLastName}
          onChange={(e) => set({ custLastName: e.target.value })}
        />
        <TextInput
          id='custMobile'
          label='Mobile'
          span={3}
          required
          type='number'
          error={errors.custMobile}
          value={newLoan.custMobile}
          onChange={(e) => {
            if (e.target.value.length <= 10) {
              set({ custMobile: e.target.value });
            }
          }}
        />
        <TextInput
          id='custEmail'
          label='Email'
          span={3}
          type='email'
          value={newLoan.custEmail}
          onChange={(e) => set({ custEmail: e.target.value })}
        />
      </FormSection>

      <FormSection title='Loan' icon={<AccountBalanceOutlined />}>
        <SelectInput
          id='loanTypeId'
          label='Loan type'
          span={4}
          required
          error={errors.loanTypeId}
          options={loanTypes.map((t) => ({ value: t.id, label: t.loanType }))}
          value={newLoan.loanTypeId}
          onChange={(e) => set({ loanTypeId: e.target.value === '#78' ? null : e.target.value })}
        />
        <CustomField id='branchIFSC' label='Branch' required span={4} error={errors.branchIFSC}>
          <Autocomplete
            id='branchIFSC'
            size='small'
            inputValue={inputValue}
            getOptionLabel={(option) => option.branchName}
            options={accessLevels}
            onInputChange={(event, newInputValue) => {
              setInputValue(newInputValue);
              getBranches(newInputValue);
            }}
            getOptionSelected={(option, value) => option.ifscCode === value.ifscCode}
            onChange={(event, element) => {
              if (element) {
                set({
                  branchIFSC: element.ifscCode,
                  branchName: element.branchName,
                });
              }
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder='Search branch'
                variant='outlined'
                error={Boolean(errors.branchIFSC)}
              />
            )}
          />
        </CustomField>
        <TextInput
          id='loanNumber'
          label='Loan number'
          span={4}
          required
          error={errors.loanNumber}
          value={newLoan.loanNumber}
          onChange={(e) => {
            let loanNumber = e.target.value;
            if (e.currentTarget.value.includes(' ')) {
              loanNumber = e.currentTarget.value.replace(/\s/g, '');
            }
            set({ loanNumber });
          }}
        />
        <TextInput
          id='sanctionedAmount'
          label='Sanctioned amount'
          span={4}
          required
          type='number'
          prefix='₹'
          error={errors.sanctionedAmount}
          value={newLoan.sanctionedAmount}
          onChange={(e) => set({ sanctionedAmount: e.target.value })}
        />
        <DateInput
          id='sanctionedDate'
          label='Sanctioned date'
          span={4}
          required
          error={errors.sanctionedDate}
          value={newLoan.sanctionedDate}
          onChange={(date) => {
            // eslint-disable-next-line no-underscore-dangle
            set({ sanctionedDate: date?._isValid ? moment(date).format('DD-MM-YYYY HH:mm:ss') : null });
          }}
        />
        <TextInput
          id='ltv'
          label='LTV'
          span={4}
          required
          type='number'
          suffix='%'
          error={errors.ltv}
          value={newLoan.ltv}
          onChange={(e) => {
            if (e.target.value < 101) {
              set({ ltv: e.target.value });
            }
          }}
        />
        <TextInput
          id='landValue'
          label='Land value'
          span={4}
          type='number'
          prefix='₹'
          value={newLoan.landValue}
          onChange={(e) => set({ landValue: e.target.value })}
        />
        <TextInput
          id='constructionValue'
          label='Construction value'
          span={4}
          type='number'
          prefix='₹'
          value={newLoan.constructionValue}
          onChange={(e) => set({ constructionValue: e.target.value })}
        />
        <TextInput
          id='propertyValue'
          label='Property value'
          span={4}
          type='number'
          prefix='₹'
          value={newLoan.propertyValue}
          onChange={(e) => set({ propertyValue: e.target.value })}
        />
      </FormSection>
    </>
  );
}
